import { Button } from '@wot/components';
import {
  IProfessionHobby,
  IProfessionSkill,
  IProfessionSubject,
  ITutor,
  ITutorAgreement,
  ITutorEducation,
  ITutorExperience,
  ITutorPersonal,
  ITutorProfessionCategory,
  ITutorRequest
} from '@wot/typings';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { toast } from 'react-toastify';
import tutorRequestsApi from '../../../../api/tutor-requests';
import tutorsApi from '../../../../api/tutors';
import { ARROWDOWN_ICON, CHECK_PENDING, CLOSE_ICON, DEFAULT_PROFILE_PIC, MORE_SVG } from '../../../../assets/icons';
import Calender from '../../../../components/Calender/Calender';
import CalenderProvider from '../../../../components/Calender/CalenderContext';
import { DropDownItem, Dropdown } from '../../../../components/DropDown';
import { Portal } from '../../../../components/Modal';
import { AddVideoLink } from './AddVideoLink';
import Agreement from './Agreement';

interface Props {
  tutor: ITutorRequest | ITutor;
  setTutor?: any;
  mode?: 'view' | 'edit';
  isRequest?: boolean;
}

export default function TutorProfile({ isRequest = true, tutor, setTutor, mode = 'view' }: Props) {
  const [openAgreement, setOpenAgreement] = useState(false);
  const [addVideoLink, setAddVideoLink] = useState(false);

  const updateTutorState = (data: any) => {
    setTutor({ ...tutor, ...data });
  }

  const verifyPersonal = async () => {
    if (isRequest) {
      await tutorRequestsApi.updateStatus({
        _id: tutor._id,
        data: {
          personal: {
            ...tutor.personal,
            status: 'verified',
            issues: []
          }
        }
      })
    } else {
      await tutorsApi.updateProfile(tutor.account_id, {
        personal: {
          ...tutor.personal,
          status: 'verified',
          issues: []
        }
      })
    }
    updateTutorState({
      personal: {
        ...tutor.personal,
        status: 'verified',
        issues: []
      }
    })
  };

  const reportPersonal = async (issues: string[]) => {
    if (isRequest) {
      await tutorRequestsApi.updateStatus({
        _id: tutor._id,
        data: {
          personal: {
            ...tutor.personal,
            status: 'issue',
            issues: issues
          }
        }
      })
    } else {
      await tutorsApi.updateProfile(tutor.account_id, {
        personal: {
          ...tutor.personal,
          status: 'issue',
          issues: issues
        }
      })
    }
    updateTutorState({
      personal: {
        ...tutor.personal,
        status: 'issue',
        issues: issues
      }
    })
  };

  const verifyEducation = async () => {
    if (isRequest) {
      await tutorRequestsApi.updateStatus({
        _id: tutor._id,
        data: {
          education: {
            ...tutor.education,
            status: 'verified',
            issues: []
          }
        }
      })
    } else {
      await tutorsApi.updateProfile(tutor.account_id, {
        education: {
          ...tutor.education,
          status: 'verified',
          issues: []
        }
      })
    }
    updateTutorState({
      education: {
        ...tutor.education,
        status: 'verified',
        issues: []
      }
    })
  };

  const reportEducation = async (issues: string[]) => {
    if (isRequest) {
      await tutorRequestsApi.updateStatus({
        _id: tutor._id,
        data: {
          education: {
            ...tutor.education,
            status: 'issue',
            issues: issues
          }
        }
      })
    } else {
      await tutorsApi.updateProfile(tutor.account_id, {
        education: {
          ...tutor.education,
          status: 'issue',
          issues: issues
        }
      })
    }
    updateTutorState({
      education: {
        ...tutor.education,
        status: 'issue',
        issues: issues
      }
    })
  };

  const verifyExperience = async () => {
    if (isRequest) {
      await tutorRequestsApi.updateStatus({
        _id: tutor._id,
        data: {
          experience: {
            ...tutor.experience,
            status: 'verified',
            issues: []
          }
        }
      })
    } else {
      await tutorsApi.updateProfile(tutor.account_id, {
        experience: {
          ...tutor.experience,
          status: 'verified',
          issues: []
        }
      })
    }
    updateTutorState({
      experience: {
        ...tutor.experience,
        status: 'verified',
        issues: []
      }
    })
  };

  const reportExperience = async (issues: string[]) => {
    if (isRequest) {
      await tutorRequestsApi.updateStatus({
        _id: tutor._id,
        data: {
          experience: {
            ...tutor.experience,
            status: 'issue',
            issues: issues
          }
        }
      })
    } else {
      await tutorsApi.updateProfile(tutor.account_id, {
        experience: {
          ...tutor.experience,
          status: 'issue',
          issues: issues
        }
      })
    }
    updateTutorState({
      experience: {
        ...tutor.experience,
        status: 'issue',
        issues: issues
      }
    })
  };

  const verifyProfession = async () => {
    if (isRequest) {
      await tutorRequestsApi.updateStatus({
        _id: tutor._id,
        data: {
          profession: {
            ...tutor.profession,
            status: 'verified',
            issues: []
          }
        }
      })
    } else {
      await tutorsApi.updateProfile(tutor.account_id, {
        profession: {
          ...tutor.profession,
          status: 'verified',
          issues: []
        }
      })
    }
    updateTutorState({
      profession: {
        ...tutor.profession,
        status: 'verified',
        issues: []
      }
    })
  };

  const reportProfession = async (issues: string[]) => {
    if (isRequest) {
      await tutorRequestsApi.updateStatus({
        _id: tutor._id,
        data: {
          profession: {
            ...tutor.profession,
            status: 'issue',
            issues: issues
          }
        }
      })
    } else {
      await tutorsApi.updateProfile(tutor.account_id, {
        profession: {
          ...tutor.profession,
          status: 'issue',
          issues: issues
        }
      })
    }
    updateTutorState({
      profession: {
        ...tutor.profession,
        status: 'issue',
        issues: issues
      }
    })
  };

  const sendToTutorDescision = async () => {
    await tutorRequestsApi.updateStatus({
      _id: tutor._id,
      data: {
        status: 'rev-agreement'
      }
    })
    updateTutorState({
      status: 'rev-agreement'
    })
  };

  const updateAgree = async (agreement: ITutorAgreement) => {
    if (isRequest) {
      tutorRequestsApi.updateAgreement({
        _id: tutor._id,
        data: {
          agreement: agreement
        }
      })
        .then(async () => {
          await sendToTutorDescision();
          toast.success("Agreement sent to tutor for Review.")
          setOpenAgreement(false);
        });
    } else {
      tutorsApi.updateAgreement({
        _id: tutor._id,
        data: {
          agreement: agreement
        }
      })
        .then(async () => {
          tutorsApi.updateProfile(tutor._id, { status: 'rev-agreement' })
          setOpenAgreement(false);
        });
    }
  };

  return (
    <div className="w-full flex flex-col gap-4 p-2 grow">
      <div className="w-full flex gap-5">
        <div className="w-1/4 h-full flex flex-col gap-7 bg-neutralBlue2 rounded-md p-4">
          <div className="w-full flex flex-col gap-7">
            {mode === 'edit' && (
              <MarkVerify
                onVerify={() => verifyPersonal()}
                onReport={reportPersonal}
                issues={tutor.personal.issues}
                status={tutor.personal.status}
              />
            )}
            <Personal personal={tutor.personal.data} />
          </div>

          <div className="w-full flex flex-col gap-7 py-4 border-t">
            {mode === 'edit' && (
              <MarkVerify
                onVerify={() => verifyEducation()}
                onReport={reportEducation}
                issues={tutor.education.issues}
                status={tutor.education.status}
              />
            )}
            <Education education={tutor.education.data} />
          </div>

          <div className="w-full flex flex-col gap-7 py-4 border-t">
            {mode === 'edit' && (
              <MarkVerify
                onVerify={() => verifyExperience()}
                onReport={reportExperience}
                issues={tutor.experience.issues}
                status={tutor.experience.status}
              />
            )}
            <Experience experience={tutor.experience.data} />
          </div>
        </div>

        <div className="w-3/4 h-full bg-white rounded-md gap-5 flex flex-col">
          <div className="w-full flex flex-col gap-2">
            <div className="w-full flex items-center justify-between bg-neutralBlue2 p-4">
              <h1 className="w-full text-lg font-semibold">What I teach</h1>
              {mode === 'edit' && (
                <MarkVerify
                  onVerify={() => verifyProfession()}
                  onReport={reportProfession}
                  issues={tutor.profession.issues}
                  status={tutor.profession.status}
                />
              )}
            </div>
            {tutor.profession.data.categoryType === 'subject' &&
              tutor.profession.data.categories.map((cate) => (
                <SubjectItem key={cate.name} subject={cate as ITutorProfessionCategory<IProfessionSubject>} />
              ))}
            {tutor.profession.data.categoryType === 'hobby' &&
              tutor.profession.data.categories.map((cate) => (
                <HobbyItem key={cate.name} hobby={cate as ITutorProfessionCategory<IProfessionHobby>} />
              ))}

            {tutor.profession.data.categoryType === 'skill' &&
              tutor.profession.data.categories.map((cate) => (
                <SkillItem key={cate.name} skill={cate as ITutorProfessionCategory<IProfessionSkill>} />
              ))}
          </div>

          {!tutor.video || tutor.video.status === 'pending' ? (
            <div className="w-full flex items-center justify-center bg-neutralGray4 p-10 rounded-md">
              <div className="max-w-sm">
                <Button
                  text="Send intro video request"
                  onClick={() => {
                    tutorRequestsApi.sendIntroVideoRequest(tutor.personal.data.email).then((res) => {
                      setTutor({
                        ...tutor,
                        video: {
                          link: "",
                          status: "requested"
                        }
                      })
                    });
                  }}
                />
              </div>
            </div>
          ) : tutor.video.status === 'requested' ? (
            <div className="w-full flex items-center justify-center bg-neutralGray4 p-10 rounded-md">
              <div className="flex justify-between gap-4 max-w-sm">
                <Button
                  text="Resend Intro Video Request"
                  type="s-outlined"
                  onClick={() => {
                    tutorRequestsApi.sendIntroVideoRequest(tutor.personal.data.email).then((res) => {
                      setTutor({
                        ...tutor,
                        video: {
                          link: "",
                          status: "requested"
                        }
                      })
                    });
                  }}
                />
                <Button
                  text="Add video link"
                  onClick={() => {
                    setAddVideoLink(true);
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="bg-neutralBlue2 p-4">
              <span className="text-xl font-medium text-black py-4">Intro Video</span>
              <div className="flex justify-center">
                <iframe className="w-96 h-64" src={tutor.video.link}></iframe>
              </div>
            </div>
          )}
          {tutor.calender && (
            <CalenderProvider>
              <Calender availableSlots={tutor.calender} />
            </CalenderProvider>
          )}
          {tutor.status === 'gen-agreement' && !tutor.agreement && (
            <>
              <div className="w-full flex items-center justify-center bg-neutralGray3 p-10 rounded-md">
                <div className="max-w-sm">
                  <Button text="Generate Agreement" onClick={() => setOpenAgreement(true)} />
                </div>
              </div>
            </>
          )}

          {tutor.agreement && (
            <div className="w-full h-full flex flex-col my-4 gap-4">
              <h1 className="w-full text-center text-xl font-medium">Tutor Agreement</h1>
              <div className="flex flex-col">
                {tutor?.agreement.document.map((item) => (
                  <div key={item.title} className="flex flex-col gap-4">
                    {item.title && <h1>{item.title}</h1>}
                    {item.contents && (
                      <div className="flex flex-col gap-2">
                        {item.contents.map((item) => (
                          <p key={item} className="text-sm">
                            {item}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="w-full max-w-[200px] mx-auto flex items-center gap-4 justify-center my-4">
                <Button text="Edit Agreement" type="s-outlined" onClick={() => setOpenAgreement(true)} />
              </div>
            </div>
          )}

          {addVideoLink && <AddVideoLink setAddVideoLink={setAddVideoLink} tutor={tutor} setTutor={setTutor} />}

          {openAgreement && (
            <Agreement handleSubmit={updateAgree} tutor={tutor} onClose={() => setOpenAgreement(false)} />
          )}
        </div>
      </div>
    </div>
  );
}

export function MarkVerify({
  onVerify,
  onReport,
  issues,
  status
}: {
  onVerify: () => void;
  onReport: (issues: string[]) => void;
  issues?: string[];
  status: string;
}) {
  const [openIssues, setOpenIssues] = useState(false);

  return (
    <div className="w-full flex items-center justify-end gap-8">
      {issues && issues?.length > 0 ? (
        <div
          onClick={() => setOpenIssues(true)}
          className="w-full flex grow items-center justify-end gap-2 cursor-pointer"
        >
          <p className="text-sm font-medium bg-red-200 text-red-700 px-2 py-1 rounded-md">Issues</p>
        </div>
      ) : (
        <>
          {status === 'verified' ? (
            <>
              <p className="text-sm font-medium bg-green-200 text-green-700 px-2 py-1 rounded-md">Verified</p>
              <div className="w-5 flex justify-end cursor-pointer">
                <Dropdown
                  menu={[
                    {
                      label: <DropDownItem label="Report Issue" onClick={() => setOpenIssues(true)} />,
                      key: 1
                    }
                  ]}
                  placement="bottomLeft"
                >
                  <div className="w-full h-full">
                    <img src={MORE_SVG} className="w-full h-4" alt="More options" />
                  </div>
                </Dropdown>
              </div>
            </>
          ) : (
            <>
              <div
                onClick={() => onVerify()}
                className="w-full flex grow items-center justify-end gap-2 cursor-pointer"
              >
                <img src={CHECK_PENDING} className="w-4" alt="Mark as verified" />
                <p className="text-sm font-medium">Mark as Verfiied</p>
              </div>
              <div className="w-5 flex justify-end">
                <Dropdown
                  menu={[
                    {
                      label: <DropDownItem label="Report Issue" onClick={() => setOpenIssues(true)} />,
                      key: 1
                    }
                  ]}
                  placement="bottomLeft"
                >
                  <div className="w-4 h-full cursor-pointer">
                    <img src={MORE_SVG} className="w-full h-4" alt="More options" />
                  </div>
                </Dropdown>
              </div>
            </>
          )}
        </>
      )}

      {openIssues && (
        <Portal>
          <WriteIssues
            issues={issues}
            onSubmit={(issues: string[]) => {
              onReport(issues);
              setOpenIssues(false);
            }}
            onCancel={() => setOpenIssues(false)}
          />
        </Portal>
      )}
    </div>
  );
}

function WriteIssues({
  issues,
  onCancel,
  onSubmit
}: {
  issues?: string[];
  onCancel: () => void;
  onSubmit: (issues: string[]) => void;
}) {
  const [tempIssues, setTempIssues] = useState<string[]>(issues || []);
  const [currentText, setCurrentText] = useState('');

  return (
    <div className="relative w-screen h-screen flex items-end">
      <div className="absolute top-0 left-0 w-full h-full bg-black/40 -z-10"></div>

      <div className="w-full h-[90%] bg-white flex flex-col items-center z-40 rounded-t-3xl">
        <div className="w-full flex items-center justify-center px-10 py-7 border-b">
          <h1 className="text-xl font-semibold mx-auto">Write Issues</h1>
          <img onClick={() => onCancel()} src={CLOSE_ICON} alt="Close" className="w-4 h-4 cursor-pointer" />
        </div>

        <div className="max-w-2xl w-full h-full max-h-full mx-auto mt-10 p-10 border border-neutralGray3 rounded-md flex flex-col justify-between gap-4">
          <div className="w-full flex flex-col gap-3 overflow-y-scroll custom-scrollbar">
            {tempIssues.map((issue, index) => (
              <div key={index} className="flex">
                <div className="w-full flex items-center gap-2 text-sm text-primary">
                  <span className="text-primary w-5 h-5 text-xs rounded-full p-1 border border-primary flex items-center justify-center">
                    {index + 1}
                  </span>
                  <p>{issue}</p>
                </div>
                <img
                  src={CLOSE_ICON}
                  className="w-3 cursor-pointer"
                  onClick={() => {
                    let issueslist = tempIssues.filter((item, idx) => index != idx);
                    setTempIssues(issueslist);
                  }}
                />
              </div>
            ))}
          </div>

          <div className="w-full flex flex-col gap-5">
            <div className="w-full flex flex-col gap-1">
              <p className="text-sm">Write Issue</p>
              <textarea
                value={currentText}
                onChange={(e) => setCurrentText(e.target.value)}
                className="w-full text-sm border px-4 py-2 outline-none min-h-[100px]"
              />
            </div>
            <div className="w-max">
              <Button
                type="s-outlined"
                onClick={() => {
                  currentText !== '' && setTempIssues([...tempIssues, currentText]);
                  setCurrentText('');
                }}
                text="+ Add Issue"
                size="sm"
              />
            </div>
          </div>
        </div>

        <div className="max-w-sm flex items-center gap-4 my-5">
          <div className="min-w-[128px]">
            <Button block type="s-outlined" text={'Cancel'} onClick={() => onCancel()} role="submit" />
          </div>
          <div className="min-w-[128px]">
            <Button block text={'Save'} onClick={() => onSubmit(tempIssues)} role="submit" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function SubjectItem({ subject }: { subject: ITutorProfessionCategory<IProfessionSubject> }) {
  const [expand, setExpand] = useState(false);
  return (
    <div
      onClick={() => setExpand(!expand)}
      className="w-full flex flex-col gap-4 p-4 bg-neutralBlue2 rounded-md cursor-pointer"
    >
      <div className="w-full flex items-center justify-between">
        <h1 className="text-base font-semibold">{subject.name}</h1>
        <img src={ARROWDOWN_ICON} className="w-4" alt="Expand" />
      </div>
      {expand && (
        <div className="w-full grid grid-cols-2 gap-4">
          {subject.board.map((item, index: any) => (
            <div className="bg-white rounded-xl flex flex-col flex-wrap gap-4 p-4 drop-shadow-lg" key={index}>
              <div className="flex flex-col">
                <p className="text-base font-medium">Board:</p>
                <p className="text-neutralGray2">{item.name}</p>
              </div>
              <div className="flex flex-col">
                <p className="text-base font-medium">Grades:</p>
                <div className="flex flex-wrap">
                  {item.grades.length > 0 &&
                    item.grades.map((i: any, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
              <div className="flex flex-col">
                <p className="text-base font-medium">Experience:</p>
                <p className="text-neutralGray2">
                  {item.experience}
                </p>
              </div>
              <div className="flex flex-col">
                <p className="text-base font-medium">Speciality:</p>
                <div className="flex flex-wrap">
                  {item.strengths.length > 0 &&
                    item.strengths.map((i: any, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function HobbyItem({ hobby }: { hobby: ITutorProfessionCategory<IProfessionHobby> }) {
  const [expand, setExpand] = useState(false);
  return (
    <div
      onClick={() => setExpand(!expand)}
      className="w-full flex flex-col gap-4 p-4 bg-neutralBlue2 rounded-md cursor-pointer"
    >
      <div className="w-full flex items-center justify-between">
        <h1 className="text-base font-semibold">{hobby.name}</h1>
        <img src={ARROWDOWN_ICON} className="w-4" alt="Expand" />
      </div>
      {expand && (
        <div className="w-full grid grid-cols-2 gap-4">
          {hobby?.types && hobby?.types?.length > 0 ? (
            <>
              {hobby.types?.map((item: any, index: any) => (
                <div className="bg-white rounded-xl flex flex-col gap-4 p-4 drop-shadow-lg" key={index}>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Type:</h3>
                    <p className="text-neutralGray2">{item.name}</p>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Levels:</h3>
                    <div className="flex flex-wrap">
                      {item.levels.length > 0 &&
                        item.levels.map((i: any, index: any) => (
                          <p className="text-neutralGray2" key={index}>
                            {index ? ', ' : ''}
                            {i}
                          </p>
                        ))}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Years of Experience:</h3>
                    <p className="text-neutralGray2">
                      {item.experience} {item.experience > 1 ? 'Years' : 'Year'}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Speciality:</h3>
                    <div className="flex flex-wrap">
                      {item.strengths.length > 0 &&
                        item.strengths.map((i: any, index: any) => (
                          <p className="text-neutralGray2" key={index}>
                            {index ? ', ' : ''}
                            {i}
                          </p>
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="bg-white rounded-xl flex flex-col gap-4 p-4 drop-shadow-lg">
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Levels:</h3>
                <div className="flex flex-wrap">
                  {hobby.levels &&
                    hobby.levels.length > 0 &&
                    hobby.levels.map((i: any, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Years of Experience:</h3>
                <p className="text-neutralGray2">
                  {hobby.experience}
                </p>
              </div>
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Speciality:</h3>
                <div className="flex flex-wrap">
                  {hobby.strengths &&
                    hobby.strengths.length > 0 &&
                    hobby.strengths.map((i: any, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SkillItem({ skill }: { skill: ITutorProfessionCategory<IProfessionSkill> }) {
  const [expand, setExpand] = useState(false);
  return (
    <div
      onClick={() => setExpand(!expand)}
      className="w-full flex flex-col gap-4 p-4 bg-neutralBlue2 rounded-md cursor-pointer"
    >
      <div className="w-full flex items-center justify-between">
        <h1 className="text-base font-semibold">{skill.name}</h1>
        <img src={ARROWDOWN_ICON} className="w-4" alt="Expand" />
      </div>
      {expand && (
        <div className="w-full grid grid-cols-2 gap-4">
          {skill?.types && skill?.types?.length > 0 ? (
            <>
              {skill.types?.map((item: any, index: any) => (
                <div className="bg-white rounded-xl flex flex-col gap-4 p-4 drop-shadow-lg" key={index}>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Type:</h3>
                    <p className="text-neutralGray2">{item.name}</p>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Levels:</h3>
                    <div className="flex flex-wrap">
                      {item.levels.length > 0 &&
                        item.levels.map((i: any, index: any) => (
                          <p className="text-neutralGray2" key={index}>
                            {index ? ', ' : ''}
                            {i}
                          </p>
                        ))}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Years of Experience:</h3>
                    <p className="text-neutralGray2">
                      {item.experience} {item.experience > 1 ? 'Years' : 'Year'}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-base font-medium">Speciality:</h3>
                    <div className="flex flex-wrap">
                      {item.strengths.length > 0 &&
                        item.strengths.map((i: any, index: any) => (
                          <p className="text-neutralGray2" key={index}>
                            {index ? ', ' : ''}
                            {i}
                          </p>
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="bg-white rounded-xl flex flex-col gap-4 p-4 drop-shadow-lg">
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Levels:</h3>
                <div className="flex flex-wrap">
                  {skill.levels &&
                    skill.levels.length > 0 &&
                    skill.levels.map((i: any, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Years of Experience:</h3>
                <p className="text-neutralGray2">
                  {skill.experience}
                </p>
              </div>
              <div className="flex flex-col">
                <h3 className="text-base font-medium">Speciality:</h3>
                <div className="flex flex-wrap">
                  {skill.strengths &&
                    skill.strengths.length > 0 &&
                    skill.strengths.map((i: any, index: any) => (
                      <p className="text-neutralGray2" key={index}>
                        {index ? ', ' : ''}
                        {i}
                      </p>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface PersonalDetails {
  personal: ITutorPersonal;
}

function Personal({ personal }: PersonalDetails) {
  return (
    <>
      <div className="w-full flex flex-col gap-2 p-2 items-center justify-center">
        <div className="w-16 h-16 rounded-full">
          <img
            src={personal?.profilePic === '' ? DEFAULT_PROFILE_PIC : personal?.profilePic}
            alt="Profile Pic"
            className="w-full h-full object-cover rounded-full"
          />
        </div>
        <div className="w-full text-center">
          <p className="text-base font-medium overflow-clip">{personal.name}</p>
        </div>
        <div className="w-full text-center">
          <p className="text-sm font-medium overflow-hidden text-ellipsis">{personal.email}</p>
          <p className="text-sm font-medium">{personal.phoneNumber}</p>
        </div>
      </div>

      <div className="w-full flex flex-col gap-4">
        <h1 className="text-lg font-medium">Personal details</h1>
        <ul className="w-full list-disc ml-4 flex flex-col gap-4">
          <li className="text-sm whitespace-pre-wrap">
            {personal.address.street}, {personal.address.houseNo}, {personal.address.city}, {personal.address.state},{' '}
            {personal.address.pincode}, {personal.address.country}
          </li>
          <li className="text-sm">Date of birth: {format(parseISO(personal.dateOfBirth), 'dd/MM/yyyy')}</li>
          <li className="text-sm">Gender: {personal.gender}</li>
          <li className="text-sm">
            <p className="text-sm font-medium">
              Name as per ID
              <br /> <span className="font-normal">{personal.proofIdName}</span>
            </p>
            <div className="w-full flex flex-col gap-4 my-2 -ml-4">
              <div className="w-full h-full bg-slate-100 min-h-32 max-h-64 ">
                <img src={personal.aadhaarIdFrontImg} alt="ID Proof Front" className="max-h-64 object-contain" />
              </div>

              <div className="w-full bg-slate-100 min-h-32 max-h-64 ">
                <img src={personal.aadhaarIdBackImg} alt="ID Proof Back" className="max-h-64 object-contain" />
              </div>

              <div className="w-full bg-slate-100 min-h-32 max-h-64 ">
                <img src={personal.panCardFrontImg} alt="ID Proof Back" className="max-h-64 object-contain" />
              </div>
            </div>
          </li>
          <li className="text-sm">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">Bio</p>
              <p className="text-sm">{personal.bio}</p>
            </div>
          </li>

          <li className="text-sm">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">Languages Spoken</p>
              <div className="flex flex-wrap">
                {personal.languagesSpoken.map((l) => (
                  <p key={l} className="text-sm">
                    {l},
                  </p>
                ))}
              </div>
              {/* <p className="text-sm whitespace-pre-wrap">{personal.languagesSpoken.map((item) => item)}</p> */}
            </div>
          </li>
        </ul>
      </div>
    </>
  );
}

interface EducationProps {
  education: ITutorEducation[];
}

function Education({ education }: EducationProps) {
  return (
    <>
      <div className="w-full flex flex-col gap-4">
        <h1 className="text-lg font-medium">Education</h1>
        <ul className="w-full list-disc ml-4 flex flex-col gap-4">
          {education.map((education, index) => (
            <li key={index} className="text-sm">
              {education.degree}, {education.course} {education.specialisation}, {education.university}, {education.year}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
interface ExperienceDetails {
  experience: ITutorExperience[];
}
function Experience({ experience }: ExperienceDetails) {
  return (
    <>
      <div className="w-full flex flex-col gap-4">
        <h1 className="text-lg font-medium">Expreience</h1>
        <ul className="w-full list-disc ml-4 flex flex-col gap-4">
          {experience.map((experience, index) => (
            <li key={index} className="text-sm">
              <div className="flex flex-col gap-2 text-sm">
                <p>{experience.organization}</p>
                <p>{experience.type}</p>
                <p>
                  {experience.workingSince.start.month}/{experience.workingSince.start.year} to{' '}
                  {experience.workingSince.end ?
                    experience.workingSince.end.month + "/" + experience.workingSince.end.year
                    :
                    "currently working"
                  }
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
